import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
// import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { toast } from 'react-toastify';
import axiosInstance from '../../../lib/axios';
import { Box, Autocomplete, TextField, Button, Typography, FormControl, InputLabel, Select, MenuItem, Tooltip, IconButton, CircularProgress, Breadcrumbs, Link } from '@mui/material';
import moment from 'moment';
import ReplayIcon from '@mui/icons-material/Replay';
import UpdateIcon from '@mui/icons-material/Update';
import { useSelector, useDispatch } from 'react-redux';
import {
    selectProperties,
    selectGlobalProperty,
    selectLocalProperty,
    setLocalProperty,
    fetchProperties
} from '../../../redux/propertySlice';
import { usePermissions } from '../../../GlobalFunction/userPermissions';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { ToastContainer } from 'react-toastify';
import HomeIcon from '@mui/icons-material/Home';

ModuleRegistry.registerModules([AllCommunityModule]);

const LeadTransferTable = () => {
    const [rowData, setRowData] = useState([]);
    const [selectedCount, setSelectedCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [propertyOptions, setPropertyOptions] = useState([]);
    const [followByOptions, setFollowByOptions] = useState([]);
    const [selectedFollowBy, setSelectedFollowBy] = useState(null);
    const [selectedNextFollowBy, setSelectedNextFollowBy] = useState(null);
    const [dataFetched, setDataFetched] = useState(false);
    const UserId = sessionStorage?.getItem('UserId');
    const permissions = usePermissions("Lead Transfer");
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const gridRef = useRef();
    const properties = useSelector(selectProperties);
    const globalProperty = useSelector(selectGlobalProperty);
    const localProperty = useSelector(state => selectLocalProperty(state, 'leadTransfer'));
    const selectedProperty = localProperty || globalProperty;

    const colDefs = useMemo(() => [
        {
            field: 'FlwEmpNm', headerName: 'Follow By', checkboxSelection: true, headerCheckboxSelection: true,
            headerCheckboxSelectionFilteredOnly: true, width: 100
        },
        { field: 'EnqNo', headerName: 'EnquiryNo.', width: 100 },
        { field: 'EnqDt', headerName: 'EnqDate', width: 100, filter: 'agDateColumnFilter', floatingFilter: true },
        { field: 'LastFlwUserName', headerName: 'LastFollowBy', width: 100 },
        {
            field: 'Status',
            headerName: 'Status',
            width: 80,
            cellStyle: params => {
                if (params.value === 'Open') {
                    return { color: 'green', fontWeight: 'bold' };
                } else if (params.value === 'Close') {
                    return { color: 'red', fontWeight: 'bold' };
                }
                return null;
            }
        },
        { field: 'EnqTypeNm', headerName: 'EnqType', width: 90 },
        {
            field: 'VisitSt',
            headerName: 'Visit',
            width: 70,
            cellStyle: params => {
                if (params.value === 'Yes') {
                    return { color: 'green', fontWeight: 'bold' };
                } else if (params.value === 'No') {
                    return { color: 'red', fontWeight: 'bold' };
                }
                return null;
            }
        },
        { field: 'Remark', headerName: 'Remark', width: 130 },
        { field: 'LastFlwDt', headerName: 'LastFollowUp', width: 100, filter: 'agDateColumnFilter', floatingFilter: true },
        { field: 'NextFlwDt', headerName: 'NextFollowUp', width: 100, filter: 'agDateColumnFilter', floatingFilter: true },
        { field: 'EnqBy', headerName: 'EnqBy', width: 100 },
        { field: 'MobileNo', headerName: 'Mobile No.', width: 100 },
        { field: 'EnqFor', headerName: 'EnqFor', width: 100 },
        { field: 'Tenure', headerName: 'Tenure', width: 70 },
        { field: 'Days', headerName: 'Days', width: 80 },
        { field: 'SourceBy', headerName: 'SourceBy', width: 80 },
        { field: 'SourceName', headerName: 'SourceName', width: 90 },
        { field: 'VisitEmpNm', headerName: 'Visit With', width: 90 },
        { field: 'VisitRemark', headerName: 'VisitRemark', width: 100 },
        { field: 'OtherRmk', headerName: 'Remark', width: 100 }
    ], []);

    const defaultColDef = useMemo(() => ({
        sortable: true,
        filter: true,
        resizable: true,
        floatingFilter: true,
        // enableRowGroup: true,
        width: 100,
        tooltipValueGetter: (params) => params.value ?? '',
    }), []);

    useEffect(() => {
        if (properties.length === 0) {
            dispatch(fetchProperties());
        } else {
            setPropertyOptions(properties);
        }
    }, [dispatch, properties.length]);

    const fetchFollowBy = async (propId) => {
        try {
            const response = await axiosInstance.post(`TrnFlwup/TrnFlwupFlwUser`, {
                PropId: propId || '',
                Flag: ''
            });
            if (response.data.Status === 0 && response.data.responseStatusCode === 1) {
                const options = response.data.Data.map(user => ({
                    ...user,
                    Name: user.UserName,
                    FlwUserId: user.FlwUserId
                }));
                setFollowByOptions(options);
                if (options.length > 0) {
                    setSelectedFollowBy(options[0]);
                    setSelectedNextFollowBy(options[0]);
                }
            } else {
                toast.info('No data found for this property.');
            }
        } catch (error) {
            toast.error('Unable to fetch follow by options');
        }
    };

    useEffect(() => {
        if (selectedProperty) {
            fetchFollowBy(selectedProperty.Id || selectedProperty);
        }
    }, [selectedProperty]);

    const fetchFollowup = async () => {
        if (!selectedProperty || !selectedFollowBy) {
            toast.error('Please select both a property and follow by option');
            return;
        }

        setIsLoading(true);
        try {
            const response = await axiosInstance.post(`TrnFlwup/GetTrnFlwupDashBoard?currentPage=1&limit=15`, {
                PageNumber: 1,
                PageSize: 15,
                SearchText: '',
                PropId: selectedProperty?.Id || selectedProperty,
                Flag: 'Open',
                FlwUserId: selectedFollowBy?.FlwUserId || ''
            });

            if (response.data.Status === 0) {
                const formattedData = response?.data?.Data?.map(item => ({
                    ...item,
                    EnqDt: item.EnqDt ? moment(item.EnqDt).format('DD-MM-YYYY') : '',
                    NextFlwDt: item.NextFlwDt ? moment(item.NextFlwDt).format('DD-MM-YYYY') : '',
                    FlwDt: item.FlwDt ? moment(item.FlwDt).format('DD-MM-YYYY') : '',
                    LastFlwDt: item.LastFlwDt ? moment(item.LastFlwDt).format('DD-MM-YYYY') : '',
                    VisitSt: item.VisitSt === 'Y' ? 'Yes' : 'No',
                    Status:
                        item.flwStatus === '0' ? 'Cancel' :
                            item.flwStatus === '1' ? 'Open' :
                                item.flwStatus === '5' ? 'Close' :
                                    item.flwStatus || 'Unknown'
                }));
                const openData = formattedData.filter(item => item.Status === 'Open');
                setRowData(openData);
                setDataFetched(true);
            } else {
                setRowData([]);
                setDataFetched(true);
                toast.info('No follow-up data found.');
            }
        } catch (error) {
            setRowData([]);
            setDataFetched(true);
            toast.error('Error while fetching the follow-up data');
        } finally {
            setIsLoading(false);
        }
    };

    const handlePropertyChange = (event) => {
        const selectedId = event.target.value;
        dispatch(setLocalProperty({
            componentId: 'leadTransfer',
            propertyId: selectedId
        }));
        setSelectedFollowBy(null);
        setSelectedNextFollowBy(null);
    };

    const handleFollowByChange = (event, newValue) => {
        setSelectedFollowBy(newValue);
    };

    const handleNextFollowByChange = (event, newValue) => {
        setSelectedNextFollowBy(newValue);
    };

    const onGridReady = useCallback((params) => {
        // No sizeColumnsToFit here to preserve flex sizing
    }, []);

    // Row checkbox selection count
    const onSelectionChanged = () => {
        const selectedRows = gridRef.current.api.getSelectedRows();
        setSelectedCount(selectedRows.length);
    };

    const handleRetrieve = () => {
        if (!selectedProperty) {
            toast.error('Please select a property');
            return;
        }
        if (!selectedFollowBy) {
            toast.error('Please select a follow by option');
            return;
        }
        fetchFollowup();
    };

    const handleBackClick = () => {
        navigate("/enquiry/pre-registration");
    };

    const handleNextClick = () => {
        navigate('/TableFollowUp');
    };

    const handleUpdate = async () => {
        if (!gridRef.current?.api) {
            toast.error('Grid is not initialized', { autoClose: 3000, });
            return;
        }

        const selectedNodes = gridRef.current.api.getSelectedNodes();
        if (selectedNodes.length === 0) {
            toast.error('Please select at least one row to update', { autoClose: 3000 });
            return;
        }

        if (!selectedFollowBy?.FlwUserId || !selectedNextFollowBy?.FlwUserId) {
            toast.error('Please select both current and next Follow By users', { autoClose: 3000 });
            return;
        }

        // Validation: Prevent update if Follow By and Next Follow By are the same
        if (selectedFollowBy.FlwUserId === selectedNextFollowBy.FlwUserId) {
            toast.error('Current and Next Follow By users cannot be the same', { autoClose: 3000 });
            return;
        }

        const payload = selectedNodes.map(node => ({
            FlwId: node.data?.FlwId || 0,
            EnqId: node.data?.EnqId,
            FlwUserId: selectedNextFollowBy.FlwUserId,
            LastFlwUserId: selectedFollowBy.FlwUserId
        }));

        const validPayload = payload.filter(item => item.EnqId && item.FlwUserId);
        if (validPayload.length === 0) {
            toast.error('No valid rows selected for update', { autoClose: 3000 });
            return;
        }

        setIsLoading(true);
        try {
            const response = await axiosInstance.post('TrnFlwup/TrnFlwupTrnsfer', validPayload);
            if (response.data.Status === 0 && response.data.responseStatusCode === 1) {
                toast.success(response.data.Message || 'Lead updated successfully', { autoClose: 3000 });
                fetchFollowup();
            } else {
                toast.error(`Failed to update Lead: ${response.data.Message || 'Unknown error'}`, { autoClose: 3000 });
            }
        } catch (error) {
            toast.error(`Error while updating the Lead: ${error.message}`, { autoClose: 3000 });
        } finally {
            setIsLoading(false);
        }
    };

    const PropertyFilterJSX = (
        <FormControl fullWidth size="small" sx={{ minWidth: 190 }}>
            <InputLabel id="property-select-label">Filter by Property</InputLabel>
            <Select
                labelId="property-select-label"
                id="property-select"
                value={selectedProperty?.Id || selectedProperty || ''}
                onChange={handlePropertyChange}
                label="Filter by Property"
            >
                {propertyOptions.map((property) => (
                    <MenuItem key={property.Id} value={property.Id}>
                        {property.Name}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );

    const FollowByFilter = (
        <Autocomplete
            id="follow-by-filter"
            options={followByOptions}
            getOptionLabel={(option) => option.UserName || ''}
            value={selectedFollowBy}
            onChange={handleFollowByChange}
            size="small"
            sx={{ minWidth: 180 }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label="Follow By"
                    variant="outlined"
                />
            )}
            isOptionEqualToValue={(option, value) => option.FlwUserId === value?.FlwUserId}
        />
    );

    const NextFollowByFilter = (
        <Autocomplete
            id="next-follow-by-filter"
            options={followByOptions}
            getOptionLabel={(option) => option.UserName || ''}
            value={selectedNextFollowBy}
            onChange={handleNextFollowByChange}
            size="small"
            sx={{ minWidth: 180 }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label="Next Follow By"
                    variant="outlined"
                />
            )}
            isOptionEqualToValue={(option, value) => option.FlwUserId === value?.FlwUserId}
        />
    );

    return (
        <Box
            sx={{
                width: '100%',
                height: '90vh',
                padding: '10px',
                backgroundColor: 'rgb(249, 249, 249)',
                position: 'relative'
            }}
        >
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
                flexWrap: 'wrap',
                rowGap: 2,
                background: 'rgb(255, 255, 255)',
                padding: '12px',
                borderRadius: '5px',
                boxShadow: 'rgb(0 0 0 / 8%)'
            }}>
                <Breadcrumbs
                    aria-label="breadcrumb"
                    separator="›"
                    sx={{
                        fontSize: { xs: "14px", sm: "16px" },
                        display: "flex",
                        flexDirection: { xs: "column", sm: "column", md: "row" }
                    }}
                >
                    <Link
                        underline="hover"
                        color="#635bff"
                        sx={{ cursor: "pointer", fontWeight: "500", display: "flex", alignItems: "center" }}
                        onClick={() => navigate('/dashboard')}
                    >
                        <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                        <Box component="span">Home</Box>
                    </Link>

                    <Link
                        underline="hover"
                        color="#635bff"
                        sx={{ cursor: "pointer", fontWeight: "500" }}
                        onClick={() => navigate('/enquiry/pre-registration')}
                    >
                        Booking(Pre-Registration)
                    </Link>
                    <Typography color="text.primary" sx={{ fontWeight: "500" }}> Lead Transfer</Typography>
                </Breadcrumbs>

                <Box sx={{ display: 'flex', gap: 1 }}>
                    {PropertyFilterJSX}
                    {FollowByFilter}
                    {NextFollowByFilter}
                    <Button
                        variant="contained"
                        onClick={handleRetrieve}
                        disabled={isLoading || !selectedProperty}
                        sx={{
                            backgroundColor: '#635bff',
                            color: '#ffffff',
                            borderRadius: '20px',
                            minWidth: '100px',
                            '&:hover': {
                                backgroundColor: '#524ccc'
                            }
                        }}
                    >
                        <ReplayIcon sx={{ fontSize: '16px', mr: 1 }} /> Retrieve
                    </Button>
                    <Tooltip title='Update folloy by' arrow>
                        <Button
                            variant="contained"
                            onClick={handleUpdate}
                            sx={{
                                backgroundColor: '#635bff',
                                color: '#ffffff',
                                borderRadius: '20px',
                                minWidth: '100px',
                                '&:hover': {
                                    backgroundColor: '#524ccc'
                                }
                            }}
                        >
                            <UpdateIcon sx={{ fontSize: '16px', mr: 1 }} />Update
                        </Button>
                    </Tooltip>
                    <Tooltip title='Main Page' arrow>
                        <Box sx={{
                            display: "flex", alignItems: "center", gap: 1,
                            justifyContent: "flex-start"
                        }}
                        >
                            <IconButton
                                onClick={handleBackClick}
                                sx={{
                                    backgroundColor: '#635bff',
                                    borderRadius: '50%',
                                    width: 40,
                                    height: 40,
                                    '&:hover': {
                                        backgroundColor: '#635bff',
                                    },
                                    boxShadow: 3,
                                }}
                            >
                                <ArrowBackIcon sx={{ color: 'white' }} />
                            </IconButton>
                        </Box>
                    </Tooltip>
                    <Tooltip title='Follow Up Table' arrow>
                        <Box sx={{
                            display: "flex", alignItems: "center", gap: 1,
                            justifyContent: "flex-start"
                        }}
                        >
                            <IconButton
                                onClick={handleNextClick}
                                sx={{
                                    backgroundColor: '#635bff',
                                    borderRadius: '50%',
                                    width: 40,
                                    height: 40,
                                    '&:hover': {
                                        backgroundColor: '#635bff',
                                    },
                                    boxShadow: 3,
                                }}
                            >
                                <ArrowForwardIcon sx={{ color: 'white' }} />
                            </IconButton>
                        </Box>
                    </Tooltip>
                </Box>
            </Box>

            <Box className="table-grid ag-theme-alpine" sx={{ height: 'calc(97% - 56px)', width: '100%', position: 'relative' }}>
                {isLoading ? (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 10,
                            textAlign: 'center',
                        }}
                    >
                        <CircularProgress size="3rem" sx={{ color: '#635bff' }} />

                        <Typography sx={{ marginTop: 2, color: '#635bff', fontWeight: 'bold' }}>
                            Loading data...
                        </Typography>
                    </Box>
                ) : (
                    <AgGridReact
                        ref={gridRef}
                        rowData={rowData}
                        columnDefs={colDefs}
                        defaultColDef={defaultColDef}
                        pagination={true}
                        paginationPageSize={1000}
                        rowSelection="multiple"
                        paginationPageSizeSelector={[50, 100, 500, 1000, 5000]}
                        onGridReady={onGridReady}
                        suppressRowClickSelection={true}
                        enableCellTextSelection={true}
                        rowHeight={25}
                        headerHeight={30}
                        floatingFiltersHeight={28}
                        onSelectionChanged={onSelectionChanged}
                        overlayNoRowsTemplate={'<span class="ag-overlay-loading-center">No rows to show</span>'}
                        tooltipShowDelay={0}
                        tooltipHideDelay={1000}
                    />
                )}
            </Box>
            <Box sx={{
                position: 'absolute',
                bottom: 15,
                left: 25,
                fontSize: '14px',
                color: 'black',
            }}>
                Selected Rows: <b>{selectedCount}</b>
            </Box>
        </Box>
    );
};

export default LeadTransferTable;